import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Send, Loader2, X, Paperclip, Mic, MicOff, FileText, Square, BookOpen } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { SlashCommandMenu, type SlashCommandItem } from './SlashCommandMenu';
import { ConnectorsMenu } from './ConnectorsMenu';
import type { WorkspaceAttachedFile } from './WorkspaceShell';

/** Task #3821 — skill richiamata esplicitamente (chip nel composer). */
export interface InvokedSkillChip {
  id: string;
  name: string;
}

export interface ChatComposerProps {
  inputValue: string;
  onInputChange: (v: string) => void;
  onSendMessage: () => void;
  onInterrupt: () => Promise<void>;
  isStreaming: boolean;
  isRecording: boolean;
  onToggleRecording: () => void;

  attachedFiles: WorkspaceAttachedFile[];
  onUploadFiles: (files: File[]) => void;
  onRemoveFile: (id: string) => void;

  /** Cambio conversazione: rifocalizza la textarea (dep dell'autofocus). */
  currentConversationId: string | null;

  // Task #3818 — menu connettori per-chat (solo restrizione del perimetro):
  /** Assistente corrente: abilita il menu connettori nel composer. */
  assistantId?: string;

  // Task #3821 — comando `/skill` (menu slash estendibile, oggi solo skill):
  /** Voci del menu slash (skill attive disponibili, incluse le condivise). */
  slashSkills?: SlashCommandItem[];
  /** Skill richiamate esplicitamente: GARANTITE attive nel prossimo turno. */
  invokedSkills?: InvokedSkillChip[];
  onInvokeSkill?: (item: SlashCommandItem) => void;
  onRemoveInvokedSkill?: (id: string) => void;

  /** Gutter orizzontale dei blocchi (default = colonna desktop ~1cm). */
  gutterClass?: string;
  /** Larghezza colonna interna (default = full-width desktop). */
  convColClass?: string;
}

// Composer condiviso (single source of truth): chips allegati + barra input
// (file input, paperclip, mic, textarea, send/stop) + autofocus. Render come
// Fragment di due blocchi fratelli: il DOM desktop resta identico (nessun
// wrapper aggiuntivo). Il drag&drop resta nel frame perché copre l'intero
// pannello chat, non solo la barra.
export const ChatComposer: React.FC<ChatComposerProps> = ({
  inputValue, onInputChange, onSendMessage, onInterrupt, isStreaming, isRecording, onToggleRecording,
  attachedFiles, onUploadFiles, onRemoveFile, currentConversationId, assistantId,
  slashSkills, invokedSkills, onInvokeSkill, onRemoveInvokedSkill,
  gutterClass = 'px-[1cm]', convColClass = 'w-full',
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Task #3821 — stato del menu slash: `/` apre la discovery delle skill;
  // il testo dopo `/` (o `/skill `) filtra per nome+descrizione. Esc chiude
  // fino alla prossima modifica dell'input.
  const [slashDismissed, setSlashDismissed] = useState(false);
  const [slashIndex, setSlashIndex] = useState(0);

  const slashQuery = useMemo(() => {
    if (!inputValue.startsWith('/') || inputValue.includes('\n')) return null;
    const raw = inputValue.slice(1);
    const m = raw.match(/^skill(?:\s+(.*))?$/i);
    return (m ? (m[1] ?? '') : raw).trimStart();
  }, [inputValue]);

  const slashItems = useMemo(() => {
    if (slashQuery === null || !slashSkills || slashSkills.length === 0) return [];
    const q = slashQuery.toLowerCase();
    const pending = new Set((invokedSkills ?? []).map(s => s.id));
    return slashSkills.filter(s =>
      !pending.has(s.id) && (q === '' || s.label.toLowerCase().includes(q) || s.description.toLowerCase().includes(q)),
    );
  }, [slashQuery, slashSkills, invokedSkills]);

  const slashOpen = slashQuery !== null && !slashDismissed && !!slashSkills && !isStreaming && !isRecording;

  useEffect(() => { setSlashDismissed(false); setSlashIndex(0); }, [inputValue]);

  const pickSlashItem = (item: SlashCommandItem) => {
    onInvokeSkill?.(item);
    onInputChange(''); // il comando slash non fa parte del messaggio
    textareaRef.current?.focus();
  };

  // Auto-focus textarea: alla fine dello streaming (la textarea è disabilitata
  // durante l'invio e perde il focus) e quando si cambia conversazione, così
  // l'utente può scrivere subito senza ricliccare.
  useEffect(() => {
    if (!isStreaming && !isRecording) {
      const t = window.setTimeout(() => textareaRef.current?.focus(), 80);
      return () => window.clearTimeout(t);
    }
  }, [isStreaming, isRecording, currentConversationId]);

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (slashOpen && slashItems.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSlashIndex(i => Math.min(i + 1, slashItems.length - 1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setSlashIndex(i => Math.max(i - 1, 0)); return; }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const item = slashItems[slashIndex] ?? slashItems[0];
        if (item) pickSlashItem(item);
        return;
      }
      if (e.key === 'Escape') { e.preventDefault(); setSlashDismissed(true); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSendMessage();
    }
  };

  const handleFileButton = () => fileInputRef.current?.click();
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) onUploadFiles(Array.from(files));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <>
      {/* Attachment chips */}
      {attachedFiles.length > 0 && (
        <div className={`${gutterClass} py-2 border-t border-border bg-muted/60`}>
          <div className={`flex flex-wrap gap-2 ${convColClass}`}>
            {attachedFiles.map((af) => (
              <div key={af.id} className="relative">
                {af.type === 'image' && af.preview ? (
                  <div className="w-12 h-12 rounded-lg overflow-hidden border border-border bg-card">
                    <img src={af.preview} alt="" className="w-full h-full object-cover" />
                    {af.uploading && (
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center rounded-lg">
                        <Loader2 className="h-4 w-4 animate-spin text-white" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 px-2 py-1.5 bg-card rounded-lg border border-border text-xs">
                    <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="max-w-[120px] truncate text-foreground">{af.file.name}</span>
                    {af.uploading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => onRemoveFile(af.id)}
                  className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center"
                  title="Rimuovi allegato"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Task #3821 — chip skill richiamate: garantite attive nel prossimo turno */}
      {invokedSkills && invokedSkills.length > 0 && (
        <div className={`${gutterClass} py-1.5 border-t border-border bg-violet-50/60`}>
          <div className={`flex flex-wrap items-center gap-1.5 ${convColClass}`}>
            <span className="text-[10px] text-violet-700 font-medium">Skill richiamate:</span>
            {invokedSkills.map(s => (
              <span key={s.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-violet-100 border border-violet-200 text-[11px] font-mono text-violet-800">
                <BookOpen className="h-3 w-3" /> {s.name}
                <button
                  type="button"
                  onClick={() => onRemoveInvokedSkill?.(s.id)}
                  className="hover:text-violet-950"
                  title="Rimuovi richiamo"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Input bar with Paperclip + Mic */}
      <div className={`relative border-t border-border ${gutterClass} py-2 bg-card`}>
        {slashOpen && (
          <SlashCommandMenu
            items={slashItems}
            highlightedIndex={Math.min(slashIndex, Math.max(slashItems.length - 1, 0))}
            onHighlight={setSlashIndex}
            onPick={pickSlashItem}
            query={slashQuery ?? ''}
          />
        )}
        <div className={`flex items-end gap-1.5 ${convColClass}`}>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/jpeg,image/png,image/gif,image/webp,.pdf,.doc,.docx,.txt"
            multiple
            onChange={handleFileChange}
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-9 w-9 p-0 shrink-0 text-muted-foreground hover:text-foreground"
            onClick={handleFileButton}
            title={isRecording ? 'Allegati disabilitati durante la registrazione' : 'Allega file'}
            disabled={isStreaming || isRecording}
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          {assistantId && (
            <ConnectorsMenu
              assistantId={assistantId}
              conversationId={currentConversationId}
              disabled={isStreaming || isRecording}
            />
          )}
          <Button
            variant="ghost"
            size="sm"
            className={`h-9 w-9 p-0 shrink-0 ${isRecording ? 'text-red-500 animate-pulse' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={onToggleRecording}
            title={isRecording ? 'Stop registrazione' : 'Registra audio'}
            disabled={isStreaming}
          >
            {isRecording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </Button>
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={handleKey}
            placeholder={isRecording ? 'Registrazione in corso…' : 'Scrivi un messaggio…'}
            disabled={isStreaming || isRecording}
            rows={1}
            className="flex-1 text-sm border border-border bg-muted rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-violet-500 focus:border-transparent disabled:opacity-50 resize-none max-h-32"
          />
          {isStreaming ? (
            <Button
              size="sm"
              className="h-9 w-9 p-0 shrink-0 bg-red-500 hover:bg-red-600"
              onClick={onInterrupt}
              title="Ferma la risposta"
            >
              <Square className="h-4 w-4 text-white fill-white" />
            </Button>
          ) : (
            <Button
              size="sm"
              className="h-9 w-9 p-0 shrink-0 bg-gray-900 hover:bg-gray-800"
              onClick={onSendMessage}
              disabled={isRecording || (!inputValue.trim() && attachedFiles.length === 0)}
              title={isRecording ? 'Invio disabilitato durante la registrazione' : 'Invia'}
            >
              <Send className="h-4 w-4 text-white" />
            </Button>
          )}
        </div>
      </div>
    </>
  );
};
